import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  AppBar,
  Toolbar,
  styled,
  useTheme,
  InputAdornment,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { loadTradingViewData, getCurrentDataFilePath } from '../utils/tradingViewDataLoader';

const PageContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  width: '100%',
  overflow: 'hidden'
});

const ContentContainer = styled('div')({
  flexGrow: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
});

const SearchBar = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
}));

const TableWrapper = styled(TableContainer)(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  overflow: 'auto',
  '& .MuiTable-root': {
    width: 'max-content',
    minWidth: '100%'
  }
}));

const StyledTable = styled(Table)(({ theme }) => ({
  borderCollapse: 'separate',
  borderSpacing: 0,
  '& th, & td': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '100px',
    maxWidth: '150px',
    padding: '8px 16px',
    fontSize: '0.7rem',
    '&.numeric-cell': {
      textAlign: 'right',
      fontFamily: theme.typography.fontFamilyMonospace
    }
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: '8px 16px',
  fontSize: '0.7rem',
  fontWeight: 500,
  position: 'sticky',
  top: 0,
  zIndex: 2,
  '&:first-of-type': {
    left: 0,
    zIndex: 3
  },
  '&.positive-value': {
    color: '#66bb6a !important'
  },
  '&.negative-value': {
    color: '#ef5350 !important'
  },
  '&.numeric-cell': {
    textAlign: 'right',
    fontFamily: theme.typography.fontFamilyMonospace
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '& td': {
    padding: '8px 12px',
    fontSize: '0.8rem',
    backgroundColor: theme.palette.background.paper,
    '&:first-of-type': {
      position: 'sticky',
      left: 0,
      zIndex: 1,
      backgroundColor: 'inherit'
    }
  }
}));

const StockPremarketPage = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [scannerData, setScannerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const columns = [
    { field: 'symbol', headerName: 'Symbol', width: 100, sticky: 'left' },
    { field: 'preMarketClose', headerName: 'Pre-market Close', width: 150 },
    { field: 'preMarketChange', headerName: 'Pre-market Chg %', width: 150 },
    { field: 'preMarketGap', headerName: 'Pre-market Gap %', width: 150 },
    { field: 'preMarketVolume', headerName: 'Pre-market Vol', width: 150 },
    { field: 'change', headerName: 'Change %', width: 120 },
    { field: 'price', headerName: 'Price', width: 100 },
    { field: 'gap', headerName: 'Gap %', width: 100 },
    { field: 'volume', headerName: 'Volume', width: 120 },
    { field: 'volPrice', headerName: 'Vol * Price', width: 120 },
    { field: 'volChange', headerName: 'Vol Change %', width: 120 },
    { field: 'postMarketClose', headerName: 'Post-market Close', width: 150 },
    { field: 'postMarketChange', headerName: 'Post-market Chg %', width: 150 },
    { field: 'postMarketVolume', headerName: 'Post-market Vol', width: 150 },
    { field: 'avgVolume10D', headerName: 'Avg Volume10D', width: 150 },
    { field: 'avgVolume30D', headerName: 'Avg Volume30D', width: 150 },
    { field: 'volatility', headerName: 'Volatility', width: 120 },
    { field: 'currentRatioFQ', headerName: 'Current ratioFQ', width: 150 },
    { field: 'debtEquityFQ', headerName: 'Debt/equityFQ', width: 150 },
    { field: 'grossMarginFY', headerName: 'Gross marginFY', width: 150 },
    { field: 'operatingMarginFY', headerName: 'Operating marginFY', width: 150 },
    { field: 'netMarginFY', headerName: 'Net marginFY', width: 150 },
    { field: 'revenueGrowthTTM', headerName: 'Revenue growthTTM YoY', width: 180 },
    { field: 'ebitdaGrowthTTM', headerName: 'EBITDA growthTTM YoY', width: 180 },
    { field: 'netIncomeGrowthTTM', headerName: 'Net income growthTTM YoY', width: 180 },
    { field: 'grossProfitGrowthTTM', headerName: 'Gross profit growthTTM YoY', width: 180 },
    { field: 'pe', headerName: 'P/E', width: 100 },
    { field: 'pegTTM', headerName: 'PEGTTM', width: 120 },
    { field: 'ps', headerName: 'P/S', width: 100 },
    { field: 'evRevenueTTM', headerName: 'EV/revenueTTM', width: 150 },
    { field: 'epsDilFQ', headerName: 'EPS dilFQ', width: 120 },
    { field: 'netIncomeFY', headerName: 'Net incomeFY', width: 150 },
    { field: 'epsEstimatesFQ', headerName: 'EPS estimatesFQ', width: 150 },
    { field: 'buybackYield', headerName: 'Buyback yield%', width: 150 },
    { field: 'roeFY', headerName: 'ROEFY', width: 120 },
    { field: 'fcfMarginFY', headerName: 'FCF marginFY', width: 150 },
    { field: 'fcfGrowthTTM', headerName: 'FCF growthTTM YoY', width: 180 },
    { field: 'epsDilGrowthTTM', headerName: 'EPS dil growthTTM YoY', width: 180 },
    { field: 'capexGrowthTTM', headerName: 'Capex growthTTM YoY', width: 180 },
    { field: 'revenueFY', headerName: 'RevenueFY', width: 150 },
    { field: 'ebitdaTTM', headerName: 'EBITDATTM', width: 150 },
    { field: 'operatingCFTTM', headerName: 'Operating CFTTM', width: 150 },
    { field: 'totalEquityFQ', headerName: 'Total equityFQ', width: 150 }
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filePath = getCurrentDataFilePath();
      console.log('Loading data from:', filePath);
      const data = await loadTradingViewData(filePath);
      setScannerData(data);
      setLastUpdate(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRestart = async () => {
    await fetchData();
  };

  // eslint-disable-next-line no-unused-vars
  const handleRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const filteredData = useMemo(() => {
    return scannerData.filter(row =>
      row.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [scannerData, searchTerm]);

  return (
    <PageContainer>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">Stock Scanner</Typography>
          {loading && (
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading...
            </Typography>
          )}
          {error && (
            <Typography variant="body2" color="error" sx={{ ml: 2 }}>
              {error}
            </Typography>
          )}
          {lastUpdate && (
            <Typography variant="body2" sx={{ ml: 2 }}>
              Last updated: {lastUpdate}
            </Typography>
          )}
        </Toolbar>
      </AppBar>

      <ContentContainer>
        <SearchBar elevation={0} sx={{ padding: '16px', flexShrink: 0 }}>
          <TextField
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search stocks..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleRestart}
            disabled={loading}
            startIcon={<RestartAltIcon />}
            sx={{ ml: 2, minWidth: 'auto' }}
          >
            Restart
          </Button>
        </SearchBar>
        
        <TableWrapper component={Paper} elevation={0}>
          <StyledTable>
            <TableHead>
              <StyledTableRow>
                {columns.map((column) => (
                  <StyledTableCell key={column.field}>{column.headerName}</StyledTableCell>
                ))}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row) => (
                <StyledTableRow
                  key={row.symbol}
                >
                  {columns.map((column) => {
                    const value = row[column.field];
                    const isNumeric = typeof value === 'number';
                    const valueClass = isNumeric
                      ? value > 0
                        ? 'positive-value numeric-cell'
                        : value < 0
                        ? 'negative-value numeric-cell'
                        : 'numeric-cell'
                      : '';

                    return (
                      <StyledTableCell 
                        key={column.field}
                        className={valueClass}
                        sx={{
                          color: isNumeric ? (
                            value > 0 ? '#66bb6a' : 
                            value < 0 ? '#ef5350' : 
                            'inherit'
                          ) : 'inherit'
                        }}
                      >
                        {value}
                      </StyledTableCell>
                    );
                  })}
                </StyledTableRow>
              ))}
            </TableBody>
          </StyledTable>
        </TableWrapper>
      </ContentContainer>
    </PageContainer>
  );
};

export default StockPremarketPage;